import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbsService } from '../../core/breadcrumbs.service';
import { FormGroup, FormControl } from '@angular/forms';
import { ScrollService } from '../../core/scroll.service';
import { AccountType } from '../../constants/account-types';
import { LoginGuardService } from '../../api/login-guard.service';
import { LangService } from '../../core/lang.service';
import { RoutesService } from '../../api/routes.service';
import { AuthService, getFrontendDomain } from '../../api/auth.service';
import { AUTH_REG_ERROR, regErrorMsgToTransSlug } from '../../api/constants/registration-validation';
import { PassValConstraint } from '../../api/constants/password-validation';
import { Subscription } from 'rxjs';
import { WhitelabelService } from 'src/app/domain/whitelabel.service'
import { IValidationError } from "../../ui-schooladmin/data/types";
import { isPasswordValid } from 'src/app/core/util/validation';

interface ICreate {
  invitationCode: string, 
  firstName: string,
  lastName: string, 
  phoneNumber?: string,
  email: string,
  password: string,
  langCode? :string
  domain? :string
}

@Component({
  selector: 'view-create-account',
  templateUrl: './view-create-account.component.html',
  styleUrls: ['./view-create-account.component.scss']
})
export class ViewCreateAccountComponent implements OnInit {
  emailConfirmed: boolean = false;
  emailAlreadyExist: boolean = false;

  constructor(
    private loginGuard: LoginGuardService, // 
    public lang: LangService, // 
    private breadcrumbsService: BreadcrumbsService,
    private router:Router,
    private route:ActivatedRoute,
    private routes:RoutesService,
    private auth:AuthService,
    private scrollService: ScrollService,
    private whitelabelService: WhitelabelService,
  ) { }

  public breadcrumb = [];
  public isFormValidated:boolean;
  public isFormFailed:boolean;
  public formFailReason:string;
  public emailUsed:string
  public isPasswordPolicyShowing:boolean;
  private passedInEmail:string;
  private passedInFirstName:string;
  private passedInLastName:string;
  private passedInInvitationCode:string;
  private routeSub:Subscription;
  public AUTH_REG_ERROR = AUTH_REG_ERROR;
  public formGroup = new FormGroup({
    invitationCode: new FormControl(),
    givenName: new FormControl(),
    surname: new FormControl(),
    phoneNumber: new FormControl(),
    emailAddress: new FormControl(),
    emailAddressAlt: new FormControl(),
    password: new FormControl(),
    passwordConfirm: new FormControl(),
  })
  public showConnectAccount = false;
  
  ngOnInit() {
    this.loginGuard.deactivate();
    this.auth.logout();
    this.scrollService.scrollToTop();
    this.breadcrumb = [
      // this.breadcrumbsService.TESTADMIN_LANDING(),
      this.breadcrumbsService._CURRENT( this.lang.tra('title_create_account'), this.router.url),
    ];
    this.routeSub = this.route.params.subscribe( routeParams => {
      const parseEncoded = (param:string) => {
        const encoded = routeParams[param];
        if (encoded){
          return decodeURIComponent(encoded);
        }
        return '';
      }
      this.passedInEmail = parseEncoded('emailEncoded');
      this.passedInFirstName = parseEncoded('firstName');
      this.passedInLastName = parseEncoded('lastName');
      this.passedInInvitationCode = routeParams['invitationCode'];
      // emailEncoded/:invitationCode
      this.initRouteView();
    });
  }

  ngOnDestroy() {
    this.routeSub.unsubscribe();
  }

  initRouteView(){
    this.formGroup.controls['invitationCode'].setValue(this.passedInInvitationCode);
    this.formGroup.controls['emailAddress'].setValue(this.passedInEmail);
    this.formGroup.controls['givenName'].setValue(this.passedInFirstName);
    this.formGroup.controls['surname'].setValue(this.passedInLastName);
  }

  togglePasswordPolicy(){
    this.isPasswordPolicyShowing = !this.isPasswordPolicyShowing;
  }

  isEqaoStyle = () => {
    return this.whitelabelService.getSiteFlag('EQAO_LOGIN')
  }

  getLoginRoute = () => {
    const homepage = window.location.host;
    const portacal = homepage.includes('localhost')?'http':'https';
    return {LOGIN_LINK: `${portacal}://${homepage}/#${this.whitelabelService.getLoginRoute(this.lang.getCurrentLanguage())}`};
    // return {LOGIN_LINK: this.whitelabelService.getLoginRoute(this.lang.getCurrentLanguage())};
  }

  /**
   * Get the login route url string
   * @returns login route url string
   */
  getLoginRouteLoginLink(){
    return this.getLoginRoute().LOGIN_LINK
  }

  isSubmitting:boolean;
  submitForm(){
    this.isSubmitting = true;
    this.isFormFailed = false;
    this.createAccount()
      .then(res => {
        this.isFormValidated = true;
        this.scrollService.scrollToTop();
        this.isSubmitting = false;
        this.emailConfirmed = res.isEmailConfirmed;
      })
      .catch(err => {
        // If email already in use, tell user to login
        if(err.message === "EMAIL_IN_USE") {
          this.isFormValidated = true; // Don't show form
          this.scrollService.scrollToTop();
          this.emailAlreadyExist = true
        } else {
          this.isFormFailed = true;
          this.formFailReason = regErrorMsgToTransSlug(err.message);
          this.refreshFailReason();
        }
        this.isSubmitting = false;
      })
  }

  isRefreshingFailReason:boolean;
  private refreshFailReason(){
    this.isRefreshingFailReason = true;
    setTimeout(() => this.isRefreshingFailReason = false, 500)
  }


  private createAccount(){

    const invitationCode = this.formGroup.controls.invitationCode.value
    const firstName = this.formGroup.controls.givenName.value
    const lastName = this.formGroup.controls.surname.value
    const phoneNumber = this.formGroup.controls.phoneNumber.value
    const email = this.formGroup.controls.emailAddress.value
    const password = this.formGroup.controls.password.value
    const passwordConfirm = this.formGroup.controls.passwordConfirm.value


    if (!invitationCode){ return Promise.reject({ message: 'MISSING_INVITATION' }) }
    if (!email){ return Promise.reject({ message: 'MISSING_EMAIL' }) }
    if (!password){ return Promise.reject({ message: 'MISSING_PASSWORD' }) }
    if (!firstName){ return Promise.reject({ message: 'MISSING_FIRSTNAME' }) }
    if (!lastName){ return Promise.reject({ message: 'MISSING_LASTNAME' }) }
    if (!password){ return Promise.reject({ message: 'MISSING_PASSWORD' }) }
    if (password !== passwordConfirm){ return Promise.reject({ message: 'PASS_MATCH' }) }
    
    if (!this.validateEmail(email)){ return Promise.reject({ message: 'EMAIL_MALFORMED' }) }

    const passwordValidation = isPasswordValid(password)
    if (!passwordValidation.isValid) {
      return Promise.reject({
        message: passwordValidation.message
      })
    }

    this.emailUsed = email;

    const req:ICreate = {
      invitationCode,
      firstName,
      lastName,
      phoneNumber,
      email,
      password,
      langCode : this.lang.c(),
      domain: getFrontendDomain()
    }

    //alert('This function has been disabled for the demo');
    //return;

    return this.auth
      .apiCreate(
        this.routes.AUTH_TEST_ADMIN,
        req,
        {}
      )
  }

  private validateEmail(email) {
    var re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  }

  connectExistingAccount(){
    this.showConnectAccount = !this.showConnectAccount;
  }

  connectAccountCancel(){
    this.showConnectAccount = false;
  }

  connectAccountOk(existingAccount){
    if(this.validateEmail(existingAccount)){
      this.auth.apiUpdate(this.routes.AUTH_TEST_ADMIN, -1, { email:existingAccount, invitationCode:this.formGroup.controls.invitationCode.value})
        .then(res =>{
          setTimeout(() => this.loginGuard.quickPopup(this.lang.tra("ca_connect_existing_account_success")), 0);
          return this.router.navigate([this.lang.c(), 'login-router-st']);
        })
        .catch(error =>{
           switch(error.message){
            case 'ERR_INVALID_CODE':
              setTimeout(() => this.loginGuard.quickPopup(this.lang.tra("txt_invitation_code_invalid")), 0);
              break;
            case 'ACCOUNT_NOT_EXIST':
              setTimeout(() => this.loginGuard.quickPopup(this.lang.tra("ca_connect_existing_account_not_existing")), 0);
              break;
            default:
              setTimeout(() => this.loginGuard.quickPopup(error.message), 0);
              break
           } 
        })
    }else{
      setTimeout(() => this.loginGuard.quickPopup(this.lang.tra("sa_invalid_email")), 0);
    }
  }
}   
